import React, { FC } from 'react';
import styled, { useTheme } from 'styled-components';
import { useTranslation } from '@blocs.i18n';
import { CastItLogo, CastItLogoDark } from '@blocs.global-header';
import { media, Theme, THEME_NAMES } from '@minecraft.themes';
import { WindowWidthContext } from '@minecraft.utils';
import { useThemeSwitcherContext } from '@minecraft.theme-switcher';
import { FEATURE_FLAGS, useFeatureExperiment } from '@blocs.features-experiments';
import { BodyTextSmall, FlexRow } from '@minecraft.atoms';

type Props = {
  fixed?: boolean;
  cdnRoot: string;
  companyName: string;
  className?: string;
  allowFormExportResponse?: boolean;
};

const FooterContainer = styled.div<{ $fixed: boolean; $isStudioTheme: boolean }>`
  color: ${(props): string => props.theme.designSystem.textColors.default};
  display: flex;
  flex-direction: ${(props) => (props.$isStudioTheme ? 'row' : 'column')};
  justify-content: space-between;
  align-items: center;
  width: 100%;
  min-height: ${(props) => (props.$isStudioTheme ? '0' : '7.5rem')};
  padding: 1.25rem 2rem;
  margin-top: auto;

  ${media.sm`
    border-top: 0.0625rem solid ${(props): string => props.theme.designSystem.borderColors.default};
    justify-content: flex-end;
    width: 100%;
    flex-direction: row;
    min-height: 4.5rem;
    position: ${({ $fixed }) => ($fixed ? 'fixed' : 'unset')};
    bottom: 0;
    left: 0;
  `}
`;

const FooterText = styled.div`
  font-style: italic;
  font-size: 0.875rem;
  align-self: center;

  ${media.sm`
    padding-right: 1rem;
  `}
`;

const CastItLogoWrapper = styled.div`
  max-width: 10rem;
`;

const CNLogoWrapper = styled.div`
  max-width: 7rem;
`;

const RecipientFooter: FC<Props> = ({
  fixed = false,
  cdnRoot,
  companyName,
  className,
  allowFormExportResponse = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme() as Theme;
  const { isDarkTheme } = useThemeSwitcherContext();
  const { lessSm } = WindowWidthContext.useWidthData();

  const isStudioTheme = theme?.name === THEME_NAMES.studio;
  const presentationFormExportFlag = useFeatureExperiment(FEATURE_FLAGS.WEB_PRESENTATION_FORMS_EXPORT);

  const logoUrl = isDarkTheme
    ? `${cdnRoot}/images/icons/b21/logo-main-nav-white.svg`
    : `${cdnRoot}/images/icons/b21/logo-main-nav.svg`;

  return (
    <FooterContainer $fixed={fixed} $isStudioTheme={isStudioTheme} className={className} data-testid="recipient-footer">
      {presentationFormExportFlag && allowFormExportResponse && (
        <BodyTextSmall textWrap="pretty" className="cn_max-w-2xl cn_atom_pl-2">
          {t('common:recipientView.formFooter')}
        </BodyTextSmall>
      )}
      <FlexRow justifyContent="end" alignItems="center">
        <FooterText>{t('common:broughtBy')}</FooterText>
        {isStudioTheme ? (
          <CastItLogoWrapper className={`${lessSm ? 'w-50 pl-2' : ''}`}>
            {isDarkTheme ? <CastItLogo /> : <CastItLogoDark />}
          </CastItLogoWrapper>
        ) : (
          <CNLogoWrapper>
            <img className="w-100" src={logoUrl} alt={companyName} title={companyName} />
          </CNLogoWrapper>
        )}
      </FlexRow>
    </FooterContainer>
  );
};

export default RecipientFooter;
